import { FiCheck } from "react-icons/fi";
import { RxDash } from "react-icons/rx";

type Step = {
  num: number;
  label: string;
  subtitle?: string;
  displayNum?: number;
};

type StepIndicatorProps = {
  steps: Step[];
  currentStep: number;
  completedSteps?: number[];
  onStepClick?: (stepNum: number) => void;
  skippedSteps?: number[];
  isStepActionable?: (stepNum: number) => boolean;
};

export function StepIndicator({
  steps,
  currentStep,
  completedSteps = [],
  onStepClick,
  skippedSteps = [],
  isStepActionable,
}: StepIndicatorProps) {
  return (
    <div
      role="list"
      aria-label="Progress"
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "8px",
        marginBottom: "24px",
        width: "100%",
        paddingLeft: "12px",
        paddingRight: "20px",
      }}
    >
      {steps.map((step, index) => {
        const isCurrent = currentStep === step.num;
        const isSkipped = skippedSteps.includes(step.num) && !isCurrent;
        const isCompleted =
          completedSteps.includes(step.num) && !isCurrent && !isSkipped;

        const stateText = isCurrent
          ? "current step"
          : isCompleted
            ? "completed"
            : isSkipped
              ? "skipped"
              : "not completed";

        const isActionable = isStepActionable
          ? isStepActionable(step.num)
          : step.num <= currentStep || completedSteps.includes(step.num);
        const isInteractive = !!onStepClick && isActionable;

        const circle = (
          <div
            aria-hidden="true"
            style={{
              width: "30px",
              height: "30px",
              fontSize: "14px",
              flexShrink: 0,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: isSkipped
                ? "transparent"
                : isCurrent
                  ? "#1e5a78"
                  : isCompleted
                    ? "#198754"
                    : "hsl(200, 15%, 96%)",
              border: isSkipped
                ? "2px dashed #6c757d"
                : isCurrent || isCompleted
                  ? "none"
                  : "1px solid hsl(210, 20%, 88%)",
              color: isSkipped
                ? "#595959"
                : isCurrent || isCompleted
                  ? "white"
                  : "#595959",
            }}
          >
            {isSkipped ? (
              <RxDash size={16} />
            ) : isCompleted ? (
              <FiCheck size={16} />
            ) : (
              (step.displayNum ?? step.num)
            )}
          </div>
        );

        const labelBlock = (
          <div
            className="text-center"
            style={{
              marginTop: "4px",
              maxWidth: "80px",
            }}
          >
            <small
              className={isCurrent ? "fw-semibold" : ""}
              style={{
                fontSize: "11px",
                lineHeight: "1.2",
                color: isSkipped ? "#595959" : isCurrent ? "#333" : "#666",
                fontWeight: isCurrent ? "600" : "500",
                display: "block",
              }}
            >
              {step.label}
            </small>
            {step.subtitle && (
              <small
                style={{
                  fontSize: "10px",
                  lineHeight: "1.2",
                  color: "#595959",
                  display: "block",
                  marginTop: "2px",
                }}
              >
                {step.subtitle}
              </small>
            )}
          </div>
        );
        const accessibleLabel = `Step ${step.displayNum ?? step.num}: ${
          step.label
        }${step.subtitle ? ` ${step.subtitle}` : ""}, ${stateText}`;

        const innerContent = (
          <>
            {circle}
            {labelBlock}
            <span className="visually-hidden">{stateText}</span>
          </>
        );

        const columnBaseStyle: React.CSSProperties = {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minWidth: "80px",
          background: "transparent",
          border: "none",
          padding: 0,
          margin: 0,
          font: "inherit",
        };

        return (
          <div
            key={step.num}
            role="listitem"
            style={{
              display: "flex",
              alignItems: "flex-start",
              flex: index < steps.length - 1 ? "1" : "0 0 auto",
            }}
          >
            {isInteractive ? (
              <button
                type="button"
                aria-label={accessibleLabel}
                aria-current={isCurrent ? "step" : undefined}
                onClick={() => onStepClick && onStepClick(step.num)}
                style={{
                  ...columnBaseStyle,
                  cursor: "pointer",
                  opacity: 1,
                  transition: "opacity 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.8";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                }}
              >
                {innerContent}
              </button>
            ) : (
              <div
                aria-current={isCurrent ? "step" : undefined}
                aria-label={accessibleLabel}
                style={columnBaseStyle}
              >
                {innerContent}
              </div>
            )}
            {index < steps.length - 1 && (
              <div
                aria-hidden="true"
                style={{
                  flex: "1",
                  height: "1px",
                  backgroundColor: isCompleted
                    ? "#198754"
                    : "hsl(210, 20%, 88%)",
                  marginTop: "15px",
                  marginLeft: "4px",
                  marginRight: "4px",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
